import REDUX_ACTION from 'modules/redux/constants/action'
import {
  REDUX_DOMAIN_ALIAS_KEY,
  REDUX_DOMAIN_TO_CLASS_NAME
} from 'modules/redux/constants'
import REDUX_OPTION from 'modules/redux/constants/option'
import { REDUX_STATUS } from '@gymaholic/packages'
import getDefaultDomainListPathFromPath from 'modules/redux/utils/get-default-domain-list-path-from-domain-path'
import getDomainMapKeyFromPathReduxUtil from 'modules/redux/utils/get-domain-map-key-from-path'
import getDomainPathFromPathReduxUtil from 'modules/redux/utils/get-domain-path-from-path'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import isInstanceReduxUtil from 'modules/redux/utils/is-instance'
import { addReduxUtil } from 'modules/redux/utils/legacy/add'
import {
  createAssociationsReduxUtil,
  createReduxUtil
} from 'modules/redux/utils/legacy/create'
import { delReduxUtil } from 'modules/redux/utils/legacy/del'
import { getDomainMapKeyFromInstancePathReduxUtil } from 'modules/redux/utils/legacy/get-domain-map-from-instance-path'
import { getMergedDataReduxUtil } from 'modules/redux/utils/legacy/get-merged-data'
import { getStatusReduxUtil } from 'modules/redux/utils/legacy/get-status'
import { hasErrorReduxUtil } from 'modules/redux/utils/legacy/has-error'
import { setReduxUtil } from 'modules/redux/utils/legacy/set'
import { updateDataReduxUtil } from 'modules/redux/utils/legacy/update-data'
import updateReduxUtil from 'modules/redux/utils/update'
import setUtil from 'utils/set'
import { removeSpacesStringUtil } from 'utils/string'
import { getObjectUtil } from 'packages/src'

function updateError (instance, error) {
  instance = setReduxUtil(instance, 'status', REDUX_STATUS.ERROR)
  return setReduxUtil(instance, 'error', error)
}

/**
 * Sets the status to the given status of the instance at the given path.
 * @param  {Object} state
 * @param  {string} path
 * @param  {REDUX_STATUS} status
 * @returns {Object}
 */
function setStatusReduxUtil (state, path, status) {
  return setReduxUtil(state, `${path}.status`, status)
}

function setSuccessReduxUtil (state, path) {
  return setStatusReduxUtil(state, path, REDUX_STATUS.SUCCESS)
}

function setLoadingReduxUtil (state, path) {
  return setStatusReduxUtil(state, path, REDUX_STATUS.LOADING)
}

function unsetStatusReduxUtil (state, path) {
  return setStatusReduxUtil(state, path, null)
}

/**
 * Loads a domain/subdomain and instance into the store.
 * @param  {Object} state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function load (state, action) {
  let {
    path,
    domainListPath,
    domainMapValue,
    options = {}
  } = action.payload
  // If the `REDUX_OPTION.OMIT_STATUS` option is provided, noop
  if (options[REDUX_OPTION.OMIT_STATUS]) {
    return state
  }
  // If the instance already exist, mark it as loading
  const instance = getObjectUtil(state, path)
  if (isInstanceReduxUtil(instance)) {
    return setLoadingReduxUtil(state, path)
  }
  // Create the Instance
  if (domainMapValue) {
    state = createInstance(state, path, domainMapValue)
  } else if (domainListPath) {
    // Create the DomainMap and DomainList
    state = createDomainMapAndList(state, path, domainListPath)
    path = domainListPath
  }
  // Mark the Instance or DomainList as loading
  return setLoadingReduxUtil(state, path)
}

/**
 * Creates the Instance.
 * @private
 * @param  {Object} state
 * @param  {string} path
 * @param  {string} domainMapValue
 * @returns {Object}
 */
function createInstance (state, path, domainMapValue) {
  // Get the className
  const domainPath = getDomainPathFromPathReduxUtil(path)
  const domainMapKey = getDomainMapKeyFromPathReduxUtil(domainPath)
  const className = REDUX_DOMAIN_TO_CLASS_NAME[domainPath]
  // Create the Instance
  const instance = createReduxUtil(className, {
    data: {
      [domainMapKey]: domainMapValue
    }
  })
  return setUtil(state, path, instance)
}

/**
 * Creates the DomainMap and DomainList.
 * @private
 * @param  {Object} state
 * @param  {string} path
 * @param  {string} domainListPath
 * @returns {Object}
 */
function createDomainMapAndList (state, path, domainListPath) {
  // Create the DomainMap
  const domainMap = getObjectUtil(state, `${path}.data`) || {}
  state = setUtil(state, `${path}.data`, domainMap)
  // Create the DomainList
  const domainList = getObjectUtil(state, domainListPath) || {
    data: []
  }
  return setUtil(state, domainListPath, domainList)
}

/**
 * Addds the instances into their respective modules and mark it as loaded.
 * NOTE: it can either mark a domain or a single instance as loaded.
 * @param  {Object} state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function loadSuccess (state, action) {
  const { path } = action.payload
  const instance = getObjectUtil(state, path)
  if (isInstanceReduxUtil(instance)) {
    return loadInstanceSuccess(state, action)
  }
  return loadDomainListSuccess(state, action)
}

/**
 * Resets the Domainlist and the data of a domain.
 * @param  {Object} state
 * @param  {string} path
 * @returns {Object} [description]
 */
function reset (state, path) {
  state = setReduxUtil(state, `${path}.all.data`, [])
  return setReduxUtil(state, `${path}.data`, {})
}

/**
 * Adds the `Instance`(s) into the `DomainList`(s) and marks them as loaded.
 * @param  {Object} state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function loadDomainListSuccess (state, action) {
  const {
    path,
    data,
    domainListPath,
    options = {}
  } = action.payload
  const newDomainListPath = (
    domainListPath ||
    getDefaultDomainListPathFromPath(path)
  )
  // If it's the last load batch
  // mark the status as `success`
  const currentStatus = getStatusReduxUtil(
    getObjectUtil(state, domainListPath)
  )
  if (
    // If there is no batching
    (
      !options[REDUX_OPTION.OMIT_STATUS] &&
      !options[REDUX_OPTION.OMIT_SUCCESS_STATUS_BATCH_SIZE]
    ) ||
    // If the `OMIT_SUCCESS_STATUS_BATCH_SIZE` option is provided
    // and the batch is less than the batch size,
    // mark the status as `success`
    (
      options[REDUX_OPTION.OMIT_SUCCESS_STATUS_BATCH_SIZE] &&
      data?.length < options[REDUX_OPTION.OMIT_SUCCESS_STATUS_BATCH_SIZE]
    )
  ) {
    state = setSuccessReduxUtil(state, newDomainListPath)
  } else if (currentStatus !== REDUX_STATUS.SUCCESS) {
    // Unset the `status`
    state = unsetStatusReduxUtil(state, newDomainListPath)
  }
  // Reset the data
  if (options[REDUX_OPTION.RESETTING]) {
    state = reset(state, path)
  }
  const domainList = getObjectUtil(state, newDomainListPath)
  if (hasErrorReduxUtil(domainList)) {
    state = setUtil(state, newDomainListPath, undefined)
  }
  // If it's an array,
  // set all the `Instance`'s `status` as `REDUX_STATUS.SUCCESS`
  if (Array.isArray(data)) {
    return loadSuccessArray(state, action)
  }
  return loadInstanceSuccess(state, action)
}

/**
 * Adds the instances into the DomainList.
 * @param  {Object} state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function loadSuccessArray (state, action) {
  let {
    path,
    data,
    domainListPath,
    className,
    options
  } = action.payload
  // Add the instances to the map and the domainListPath
  return data.reduce((updatedState, data) => {
    className = className || data.className
    const instance = createReduxUtil(className, {
      data,
      status: REDUX_STATUS.SUCCESS
    })
    updatedState = addReduxUtil({
      state: updatedState,
      path,
      instance,
      domainListPath,
      options
    })
    // If the alias should be set, set it
    if (options[REDUX_OPTION.SET_ALIAS]) {
      updatedState = setAlias(updatedState, instance, path)
    }
    return updatedState
  }, state)
}

/**
 * Sets the alias subdomain.
 * NOTE: Currently only used for matching categories url to domain key.
 *
 * e.g.
 * `iamagymaholic` in the url.
 * `i am a gymaholic` in the domain.
 * @private
 * @param  {Object} state
 * @param  {Category} instance
 * @param  {string} path
 * @returns {Object}
 */
function setAlias (state, instance, path) {
  const aliasPath = `${path}.${REDUX_DOMAIN_ALIAS_KEY}`
  const alias = getObjectUtil(state, aliasPath) || {}
  const domainMapKey = getDomainMapKeyFromInstancePathReduxUtil(
    getPathReduxUtil(instance)
  )
  return setUtil(state, aliasPath, {
    ...alias,
    [removeSpacesStringUtil(domainMapKey)]: domainMapKey
  })
}

/**
 * Sets the instance as loaded to the given path.
 * @private
 * @param  {Object}  state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function loadInstanceSuccess (state, action) {
  let {
    path,
    data,
    className,
    associatedPath,
    domainListPath,
    instanceKey,
    options
  } = action.payload

  // Get the instance
  let instance = getObjectUtil(state, path)
  className = className || getObjectUtil(instance, 'className') || data.className
  const status = REDUX_STATUS.SUCCESS

  // If an instance key is provided, only update the given key
  if (instanceKey) {
    data = {
      ...instance.data,
      [instanceKey]: data
    }
  }

  // If the instance is set to null, delete it
  if (!data && !instanceKey && isInstanceReduxUtil(instance)) {
    return setUtil(state, path, undefined)
  }

  if (isInstanceReduxUtil(instance)) {
    // Create the associations
    data = createAssociationsReduxUtil({
      data,
      status,
      className
    })
    instance = updateDataReduxUtil(instance, data)
    instance = setUtil(instance, 'className', className)
  } else {
    // If the instance doesn't exist, create it
    instance = createReduxUtil(className, {
      data,
      status
    })
  }

  instance = setReduxUtil(instance, 'status', status)
  // Add the instance to the domain
  return addReduxUtil({
    state,
    path,
    instance,
    associatedPath,
    domainListPath,
    options
  })
}

/**
 * Sets the error to the instance at the given path.
 * @param  {Object}  state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function loadError (state, action) {
  const {
    path,
    error,
    domainListPath,
    options = {}
  } = action.payload
  // If it's a instance, set it as error
  let instance = getObjectUtil(state, path)
  if (isInstanceReduxUtil(instance)) {
    instance = updateError(instance, error)
    return setUtil(state, path, instance)
  }
  // Reset the data anyway
  if (options[REDUX_OPTION.RESETTING]) {
    state = setUtil(state, `${path}.data`, {})
    state = setUtil(state, `${domainListPath}.data`, [])
  }
  const domainList = getObjectUtil(state, domainListPath)
  return setUtil(state, domainListPath, {
    ...domainList,
    error: { ...error },
    status: REDUX_STATUS.ERROR
  })
}

/**
 * Sets a instance in a saving state.
 * @param  {Object} state
 * @param  {ReduxAction} action
 *         {string} path
 * @returns {Object}
 */
function save (state, action) {
  const { path } = action.payload
  return saveFn(state, path)
}

/**
 * Sets a instance in a saving status and resets its `validation` key.
 * @param  {Object} state
 * @param  {string} path
 * @returns {Object}
 */
function saveFn (state, path) {
  const instance = getObjectUtil(state, path)
  return setUtil(state, path, {
    ...instance,
    status: REDUX_STATUS.SAVING,
    validation: null
  })
}

/**
 * Merges the instance's old and new data, then marks its status as loaded:
 * `REDUX_STATUS.SUCCESS`.
 * The instance is finally normalized and added to its domain.
 * @param  {Object} state
 * @param  {ReduxAction} action
 * @returns {Object}
 */
function saveSuccess (state, action) {
  return loadInstanceSuccess(state, action)
}

function saveError (state, action) {
  const {
    path,
    error
  } = action.payload
  const storeInstance = getObjectUtil(state, path)
  return setUtil(state, path, {
    ...storeInstance,
    error: { ...error },
    status: REDUX_STATUS.ERROR
  })
}

/**
 * Marks all the instances at the given `action.payload.paths`
 * as saving.
 * @param  {Object} state
 * @param  {Object} action
 *         {Object} action.payload
 *         {String[]} action.payload.paths
 * @returns {Object}
 */
function bulkSave (state, action) {
  const { paths } = action.payload
  return paths.reduce((state, path) => saveFn(state, path), state)
}

function bulkSaveSuccess (state, action) {
  const {
    paths,
    domainMap
  } = action.payload
  return paths.reduce((state, path) => {
    const instance = getObjectUtil(state, path)
    const instanceData = instance.data
    const id = instanceData.id
    const savedInstanceData = domainMap[id]
    const data = getMergedDataReduxUtil(instance, savedInstanceData)
    return setUtil(state, path, {
      ...instance,
      data,
      status: REDUX_STATUS.SUCCESS,
      changed: {}
    })
  }, state)
}

function bulkSaveError (state, action) {
  const {
    path,
    error
  } = action.payload
  const storeInstance = getObjectUtil(state, path)
  return setUtil(state, path, {
    ...storeInstance,
    error: { ...error },
    status: REDUX_STATUS.ERROR
  })
}

/**
 * Marks the `Instance` at the given path as `REDUX_STATUS.SAVING`.
 * @param  {Object} state
 * @param  {Object} action
 * @returns {Object}
 */
function delReducer (state, action) {
  const { path } = action.payload
  const instance = getObjectUtil(state, path)
  return setUtil(state, path, {
    ...instance,
    status: REDUX_STATUS.SAVING
  })
}

function delSuccess (state, action) {
  const {
    path,
    domainListPath
  } = action.payload
  return delReduxUtil(state, path, domainListPath)
}

function delError (state, action) {
  const {
    path,
    error
  } = action.payload
  const storeInstance = getObjectUtil(state, path)
  return setUtil(state, path, {
    ...storeInstance,
    error: { ...error },
    status: REDUX_STATUS.ERROR
  })
}

function updateReducer (state, action) {
  return updateFn({
    state, ...action.payload
  })
}

/**
 * Updates the instance's data `key` at the given `path`.
 * If the `key` is omitted, the `value` at the given `path`
 * will be replaced.
 * @private
 * @param  {Object} params
 * @param  {Object} state
 * @param  {string} path
 * @param  {*} value
 * @param  {string} key
 * @param  {boolean} [isLocal]
 * @param  {Object} updates
 * @returns {Object}
 */
function updateFn (params) {
  let {
    key,
    path,
    state,
    value,
    isLocal,
    updates
  } = params
  // If no key is provided, update the value directly
  if (!key && !updates) {
    return setUtil(state, path, value)
  }
  let instance = getObjectUtil(state, path)
  // If the key is provided and there is no `updates`, set the key's `value`
  // `setUtil` makes sure that any `undefined` value is removed
  if (key) {
    const dataPath = isLocal
      ? 'data'
      : 'changed'
    // TODO fix
    state = setUtil(state, path, updateReduxUtil(instance, {}))
    return setUtil(state, `${path}.${dataPath}.${key}`, value)
  }
  // If it's local modification, update the `data`
  if (isLocal) {
    instance = updateDataReduxUtil(instance, updates)
  } else {
    // Update the `changed` attribute
    instance = updateReduxUtil(instance, updates)
  }
  // Return the updated state
  return setUtil(state, path, instance)
}

/**
 * Updates a list of instances at the given `action.payload.paths`
 * with the given `action.payload.updates`.
 * @param  {Object} state
 * @param  {Object} action
 *         {Object} action.payload
 *         {String[]} action.payload.paths
 *         {Object[]} action.payload.updates
 * @returns {Object}
 */
function bulkUpdateReducer (state, action) {
  const {
    paths,
    updates
  } = action.payload
  return paths.reduce((state, path, index) => {
    return updateFn({
      state,
      path,
      updates: updates[index]
    })
  }, state)
}

function discard (state, action) {
  const path = action.payload.path
  const storeInstance = getObjectUtil(state, path)
  return setUtil(state, path, {
    ...storeInstance,
    changed: {},
    status: null,
    validation: null,
    error: null
  })
}

function validationSuccess (state, action) {
  const { path } = action.payload
  return setUtil(state, `${path}.validation`, true)
}

function validationError (state, action) {
  return setUtil(
    state,
    `${action.payload.path}.validation`,
    action.payload.validation
  )
}

/**
 * Returns the update of the current state and the given one.
 * @param {Object} state
 * @param {Object} action
 * @returns {Object}
 */
function set (state, action) {
  const newState = action.payload.state
  return {
    ...state,
    ...newState
  }
}

export default {
  [REDUX_ACTION.LOAD]: load,
  [REDUX_ACTION.LOAD_SUCCESS]: loadSuccess,
  [REDUX_ACTION.LOAD_ERROR]: loadError,
  [REDUX_ACTION.SAVE]: save,
  [REDUX_ACTION.SAVE_SUCCESS]: saveSuccess,
  [REDUX_ACTION.SAVE_ERROR]: saveError,
  [REDUX_ACTION.BULK_SAVE]: bulkSave,
  [REDUX_ACTION.BULK_SAVE_SUCCESS]: bulkSaveSuccess,
  [REDUX_ACTION.BULK_SAVE_ERROR]: bulkSaveError,
  [REDUX_ACTION.DELETE]: delReducer,
  [REDUX_ACTION.DELETE_SUCCESS]: delSuccess,
  [REDUX_ACTION.DELETE_ERROR]: delError,
  [REDUX_ACTION.UPDATE]: updateReducer,
  [REDUX_ACTION.BULK_UPDATE]: bulkUpdateReducer,
  [REDUX_ACTION.DISCARD]: discard,
  [REDUX_ACTION.VALIDATION_SUCCESS]: validationSuccess,
  [REDUX_ACTION.VALIDATION_ERROR]: validationError,
  [REDUX_ACTION.SET]: set
}
